export class StudioApiError extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = 'StudioApiError';
    }
}
/**
 * StudioApiClient is the single typed channel Backstage plugins use to reach the Go Control
 * Plane (ADR-016/017). It never talks to Studio databases directly and always sends
 * tenant/project scope — authorization is enforced by the Control Plane, not by the UI.
 */
export class StudioApiClient {
    options;
    baseUrl;
    doFetch;
    constructor(options) {
        this.options = options;
        this.baseUrl = options.baseUrl.replace(/\/+$/, '');
        // Native fetch must be invoked with `this === window`; called as a bare method (`this.doFetch`)
        // the browser throws "Illegal invocation". Bind the default to the global scope. An injected
        // fetch (tests) is used as given.
        this.doFetch = options.fetch ?? fetch.bind(globalThis);
    }
    headers(extra) {
        return {
            Authorization: `Bearer ${this.options.token}`,
            'X-S2-Tenant-ID': this.options.tenantId,
            'X-S2-Project-ID': this.options.projectId,
            ...extra,
        };
    }
    async get(path) {
        const response = await this.doFetch(`${this.baseUrl}${path}`, {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) {
            throw new StudioApiError(`GET ${path} failed`, response.status);
        }
        return (await response.json());
    }
    async post(path, body) {
        const response = await this.doFetch(`${this.baseUrl}${path}`, {
            method: 'POST',
            headers: this.headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            let message = `POST ${path} failed (${response.status})`;
            try {
                const problem = (await response.json());
                if (problem.error)
                    message = problem.error;
            }
            catch {
                /* keep default */
            }
            throw new StudioApiError(message, response.status);
        }
        return (await response.json());
    }
    /**
     * The signed-in identity as resolved by the Control Plane in the current scope (Auth C).
     * Behind the backend identity proxy the subject is server-asserted; the UI only displays it.
     */
    me() {
        return this.get('/v1/studio/me');
    }
    listCapacity() {
        return this.get('/v1/studio/capacity');
    }
    listWorkers() {
        return this.get('/v1/workers');
    }
    /**
     * Operator drain/release. Mirrors packages/persistence.DurableWorkerRegistry.SetState
     * (docs/WORKER-MESH.md): only AVAILABLE|BUSY→DRAINING and DRAINING→AVAILABLE are accepted —
     * every other transition, including release from QUARANTINED/DEGRADED/OFFLINE, is rejected with
     * a 409 StudioApiError. The caller is expected to gate which action it offers (see
     * apps/studio-portal/plugins/studio-home/src/workerLifecycle.ts) rather than rely on the 409.
     */
    setWorkerState(id, state) {
        return this.send('PUT', `/v1/workers/${encodeURIComponent(id)}/state`, { state });
    }
    /** Pin a worker's desired version set (e.g. workflow_build) for the next reconcile/heartbeat. */
    setWorkerDesired(id, versions) {
        return this.send('PUT', `/v1/workers/${encodeURIComponent(id)}/desired`, versions);
    }
    listProjects() {
        return this.get('/v1/projects');
    }
    /** Every project in the caller's tenant — the project switcher's source. */
    listTenantProjects() {
        return this.get('/v1/studio/projects');
    }
    listClients() {
        return this.get('/v1/clients');
    }
    listEpics() {
        return this.get('/v1/epics');
    }
    listFeatures() {
        return this.get('/v1/features');
    }
    listPlaybooks() {
        return this.get('/v1/intake/playbooks');
    }
    /** Full migrated profile→playbook set: each selectable Playbook with its ordered steps. */
    listPlaybookDefinitions() {
        return this.get('/v1/intake/playbook-definitions');
    }
    /** Admit a Feature through intake: selects a Playbook and seeds repo ref + RAG namespace. */
    createIntake(request) {
        return this.post('/v1/intake', request);
    }
    listTasks() {
        return this.get('/v1/tasks');
    }
    listHandoffs() {
        return this.get('/v1/handoffs');
    }
    updateStatus() {
        return this.get('/v1/updates/status');
    }
    listApprovals() {
        return this.get('/v1/approvals');
    }
    async decideApproval(id, decision) {
        const response = await this.doFetch(`${this.baseUrl}/v1/approvals/${encodeURIComponent(id)}/decision`, {
            method: 'POST',
            headers: this.headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(decision),
        });
        if (!response.ok) {
            let message = `decision failed (${response.status})`;
            try {
                const body = (await response.json());
                if (body.error)
                    message = body.error;
            }
            catch {
                /* keep default */
            }
            throw new StudioApiError(message, response.status);
        }
    }
    // --- Project Workboard (A7 / Phase 28) ---
    // The actor type flows in the X-S2-Actor-Type header so the Control Plane transition policy can refuse
    // an AI-forced DONE. The portal UI is HUMAN; the studio.work.* MCP path sets AGENT/WORKFLOW.
    async send(method, path, body, actorType) {
        const extra = {};
        if (body !== undefined)
            extra['Content-Type'] = 'application/json';
        if (actorType)
            extra['X-S2-Actor-Type'] = actorType;
        const response = await this.doFetch(`${this.baseUrl}${path}`, {
            method,
            headers: this.headers(extra),
            body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!response.ok) {
            let message = `${method} ${path} failed (${response.status})`;
            try {
                const problem = (await response.json());
                if (problem.error)
                    message = problem.error;
            }
            catch {
                /* keep default */
            }
            throw new StudioApiError(message, response.status);
        }
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        return (text ? JSON.parse(text) : undefined);
    }
    /** Grouped board (BACKLOG…DONE) for the scoped project. */
    getWorkboard() {
        return this.get('/v1/studio/work');
    }
    /** Flat backlog/list view of every work item in scope, newest first. */
    listWorkTasks() {
        return this.get('/v1/studio/work/tasks');
    }
    /** Full task-detail projection: task + dependencies + typed links + activity feed. */
    getWorkTask(id) {
        return this.get(`/v1/studio/work/tasks/${encodeURIComponent(id)}`);
    }
    /** Create a work item (starts in BACKLOG). */
    createWorkTask(task, actorId) {
        return this.post('/v1/studio/work/tasks', { task, actor_id: actorId });
    }
    /** Update mutable metadata (title/priority/assignee/labels); never WorkState. */
    patchWorkTask(id, patch) {
        return this.send('PATCH', `/v1/studio/work/tasks/${encodeURIComponent(id)}`, patch);
    }
    /** Request a policy-gated WorkState change. A 409 StudioApiError carries the policy reason. */
    transitionWorkTask(id, to, opts) {
        return this.send('POST', `/v1/studio/work/tasks/${encodeURIComponent(id)}/transition`, { to, note: opts?.note, actor_id: opts?.actorId }, opts?.actorType);
    }
    addWorkDependency(id, toTaskId, relation, reason) {
        return this.send('POST', `/v1/studio/work/tasks/${encodeURIComponent(id)}/dependencies`, {
            to_task_id: toTaskId,
            relation,
            reason,
        });
    }
    addWorkComment(id, body, actorId) {
        return this.send('POST', `/v1/studio/work/tasks/${encodeURIComponent(id)}/comments`, {
            body,
            actor_id: actorId,
        });
    }
    // --- Documents (per-project requisites, templates, and generated documents) ---
    async put(path, body) {
        const response = await this.doFetch(`${this.baseUrl}${path}`, {
            method: 'PUT',
            headers: this.headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            let message = `PUT ${path} failed (${response.status})`;
            try {
                const problem = (await response.json());
                if (problem.error)
                    message = problem.error;
            }
            catch {
                /* keep default */
            }
            throw new StudioApiError(message, response.status);
        }
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        return (text ? JSON.parse(text) : undefined);
    }
    async getBlob(path) {
        const response = await this.doFetch(`${this.baseUrl}${path}`, {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) {
            throw new StudioApiError(`GET ${path} failed`, response.status);
        }
        return response.blob();
    }
    /** Get project requisites (shared data for document generation). */
    getDocumentRequisites() {
        return this.get('/v1/studio/documents/requisites');
    }
    /** Update project requisites. */
    updateDocumentRequisites(data) {
        return this.put('/v1/studio/documents/requisites', { data });
    }
    /** List available document templates (tenant-wide and project-scoped). */
    listDocumentTemplates() {
        return this.get('/v1/studio/documents/templates').then(r => r.templates);
    }
    /** Create a new document template. */
    createDocumentTemplate(template) {
        return this.post('/v1/studio/documents/templates', template);
    }
    /** List generated documents in this project. */
    listDocuments() {
        return this.get('/v1/studio/documents').then(r => r.documents);
    }
    /** Create a new document (starts in DRAFT). */
    createDocument(doc) {
        return this.post('/v1/studio/documents', doc);
    }
    /** Get a specific document with full metadata and renditions. */
    getDocument(id) {
        return this.get(`/v1/studio/documents/${encodeURIComponent(id)}`);
    }
    /** Download a document in the specified format. Returns a Blob for browser save. */
    async downloadDocument(id, format) {
        return this.getBlob(`/v1/studio/documents/${encodeURIComponent(id)}/download?format=${encodeURIComponent(format)}`);
    }
    /** Dispatch a generation task onto the mesh: fills the template from requisites+params and renders. */
    generateDocument(req) {
        return this.post('/v1/studio/documents/generate', req);
    }
    /** Dispatch a revision (правки) task: the agent edits the document's current content per the feedback. */
    reviseDocument(id, feedback, opts) {
        return this.post(`/v1/studio/documents/${encodeURIComponent(id)}/revise`, { feedback, ...opts });
    }
    /** Register or update a document rendition (e.g. after generation). */
    updateDocumentRendition(docId, rendition) {
        return this.post(`/v1/studio/documents/${encodeURIComponent(docId)}/renditions`, rendition);
    }
}
